import { CreditNoteDocButtons } from "./CreditNoteDocButtons.js";
import { useEffect, useState } from "react";
import { useModals } from "../common/ModalsProvider.js";
import { trpc } from "../../lib/trpc.js";
import { formatMoney } from "../../lib/format-fr.js";
import { useT } from "../../i18n/index.js";

function parseDecimalInput(s: string): number {
  const cleaned = s.replace(/\s/g, "");
  if (!cleaned) return NaN;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized: string;
  if (lastComma > lastDot) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = cleaned.replace(/,/g, "");
  } else {
    normalized = cleaned;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Avoir (nota de crédito) sobre uma fatura de cliente. Vivia dentro de
 * Faturas.tsx; CEO 2026-10-19: o dossier (aba Faturação cliente) também
 * precisa fazer avoir, no modelo do eDSP (completo / parcial + motivo).
 */
export function CreditNoteModal({
  companyId,
  invoice,
  onClose,
  reasonRequired = false,
}: {
  companyId: number;
  /** CEO 2026-10-29 — avoir emitido pelo gerente de chantier: motivo obrigatório. */
  reasonRequired?: boolean;
  /** `totalHt` (HT da fatura) liga os atalhos "Avoir complet / partiel". */
  invoice: { id: number; invoiceNumber: string; totalHt?: number };
  onClose: () => void;
}) {
  const t = useT();
  const modals = useModals();
  const utils = trpc.useUtils();
  const listQuery = trpc.customerCreditNotes.list.useQuery({ companyId, invoiceId: invoice.id });
  const suggestQuery = trpc.customerCreditNotes.suggestNextNumber.useQuery({ companyId });
  const existing = listQuery.data ?? [];
  // CEO 2026-10-19 (modelo eDSP) — "Avoir complet" = o que ainda não foi
  // creditado da fatura; "partiel" = digitar o valor.
  const jaCreditado = existing
    .filter((a) => a.status === "issued")
    .reduce((x, a) => x + Number(a.totalHt), 0);
  const restante =
    invoice.totalHt != null
      ? Math.max(0, Math.round((invoice.totalHt - jaCreditado) * 100) / 100)
      : null;
  const [modo, setModo] = useState<"complet" | "partiel" | null>(null);

  const [num, setNum] = useState("");
  const [numEdited, setNumEdited] = useState(false);
  const [creditType, setCreditType] = useState<"commercial" | "return" | "error" | "irrecoverable">(
    "commercial",
  );
  const [ht, setHt] = useState("");
  // CEO 2026-11-02 — o TTC também se digita; o campo digitado por último manda.
  const [ttcIn, setTtcIn] = useState("");
  const [fonte, setFonte] = useState<"ht" | "ttc">("ht");
  const [tvaRate, setTvaRate] = useState("20");
  const [reason, setReason] = useState("");
  const [issuedAt, setIssuedAt] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!numEdited && suggestQuery.data) setNum(suggestQuery.data);
  }, [suggestQuery.data, numEdited]);

  const createMut = trpc.customerCreditNotes.create.useMutation({
    onSuccess: (res) => {
      void utils.customerCreditNotes.list.invalidate();
      void utils.treasury.invalidate();
      void utils.customerInvoices.list.invalidate();
      void modals.alert({
        title: t("invoices.avoirCreatedTitle"),
        message: `${res.creditNoteNumber}${t("invoices.avoirCreatedMid")}${formatMoney(res.totalTtc, { decimals: 2 })}${t("invoices.avoirCreatedSuf")}`,
      });
      setHt("");
      setTtcIn("");
      setFonte("ht");
      setReason("");
      setNumEdited(false);
      void suggestQuery.refetch();
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.avoirCreateErrorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const deleteMut = trpc.customerCreditNotes.delete.useMutation({
    onSuccess: () => {
      void utils.customerCreditNotes.list.invalidate();
      void utils.treasury.invalidate();
      void utils.customerInvoices.list.invalidate();
      void suggestQuery.refetch();
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.avoirDeleteErrorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  async function handleDelete(a: { id: number; creditNoteNumber: string }) {
    const ok = await modals.confirm({
      title: t("invoices.avoirDeleteConfirmTitle"),
      message: `${t("invoices.avoirDeleteConfirmPre")}${a.creditNoteNumber}${t("invoices.avoirDeleteConfirmSuf")}`,
      confirmLabel: t("invoices.deleteConfirm"),
      tone: "danger",
    });
    if (ok) deleteMut.mutate({ companyId, id: a.id });
  }

  const typeLabel = (ct: string) =>
    ({
      commercial: t("invoices.typeCommercial"),
      return: t("invoices.typeReturn"),
      error: t("invoices.typeError"),
      irrecoverable: t("invoices.typeIrrecoverable"),
    })[ct] ?? ct;

  const rateNum = parseDecimalInput(tvaRate);
  const fator = 1 + (Number.isFinite(rateNum) ? rateNum : 0) / 100;
  const ttcNum = parseDecimalInput(ttcIn);
  const htNum = fonte === "ttc" ? ttcNum / fator : parseDecimalInput(ht);
  const fmt2 = (n: number) => (Number.isFinite(n) ? n.toFixed(2).replace(".", ",") : "");
  /** Digitou o HT: o TTC acompanha. */
  const mudarHt = (v: string) => {
    setHt(v);
    setFonte("ht");
    const h = parseDecimalInput(v);
    setTtcIn(v.trim() && Number.isFinite(h) ? fmt2(h * fator) : "");
  };
  /** Digitou o TTC: o HT acompanha (o servidor grava o TTC exato). */
  const mudarTtc = (v: string) => {
    setTtcIn(v);
    setFonte("ttc");
    const c = parseDecimalInput(v);
    setHt(v.trim() && Number.isFinite(c) ? fmt2(c / fator) : "");
  };
  const valid =
    (!reasonRequired || reason.trim().length >= 3) &&
    num.trim().length > 0 &&
    Number.isFinite(htNum) &&
    htNum > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(issuedAt);

  const inputCls =
    "mt-1 w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold">{t("invoices.avoirModalTitle")}</h2>
        <p className="mt-1 text-xs text-neutral-500">
          {t("invoices.emailModalInvoicePre")}{" "}
          <span className="font-mono text-lime">{invoice.invoiceNumber}</span>
        </p>

        {existing.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-md border border-neutral-800">
            <table className="w-full text-xs">
              <thead className="bg-neutral-950/50 text-neutral-500">
                <tr>
                  <th className="px-2 py-1 text-left">{t("invoices.colNum")}</th>
                  <th className="px-2 py-1 text-left">{t("invoices.colType")}</th>
                  <th className="px-2 py-1 text-right">{t("invoices.colHt")}</th>
                  <th className="px-2 py-1 text-right">{t("invoices.colTtc")}</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {existing.map((a) => (
                  <tr key={a.id}>
                    <td className="px-2 py-1 font-mono text-amber-300">{a.creditNoteNumber}</td>
                    <td className="px-2 py-1">
                      {typeLabel(a.creditType)}
                      {/* F-avoir-saldo — avoir importado do eDSP já descontado
                          na fatura, ou anulado: registrado, fora do saldo. */}
                      {a.status === "embedded" && (
                        <span className="ml-1 text-neutral-500">
                          · {t("invoices.avoirStatusEmbedded")}
                        </span>
                      )}
                      {a.status === "cancelled" && (
                        <span className="ml-1 text-red-400">
                          · {t("invoices.avoirStatusCancelled")}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">
                      {formatMoney(a.totalHt, { decimals: 2 })}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">
                      {formatMoney(a.totalTtc, { decimals: 2 })}
                    </td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">
                      {/* CEO 2026-10-29 — PDF/e-mail do avoir logo após criar. */}
                      {a.status === "issued" && (
                        <span className="mr-2 inline-flex gap-1">
                          <CreditNoteDocButtons
                            companyId={companyId}
                            creditNoteId={a.id}
                            creditNoteNumber={a.creditNoteNumber}
                          />
                        </span>
                      )}
                      <button
                        onClick={() => void handleDelete(a)}
                        disabled={deleteMut.isPending}
                        className="text-red-400 hover:text-red-300 disabled:opacity-40"
                        title={t("invoices.avoirDeleteTitle")}
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="text-sm text-neutral-300">
            {t("invoices.avoirNumberLabel")}
            <input
              value={num}
              onChange={(e) => {
                setNum(e.target.value);
                setNumEdited(true);
              }}
              placeholder="A-2026-0001"
              className={inputCls + " font-mono"}
            />
          </label>
          <label className="text-sm text-neutral-300">
            {t("invoices.colDate")}
            <input
              type="date"
              value={issuedAt}
              onChange={(e) => setIssuedAt(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="text-sm text-neutral-300">
            {t("invoices.colType")}
            <select
              value={creditType}
              onChange={(e) => setCreditType(e.target.value as typeof creditType)}
              className={inputCls}
            >
              <option value="commercial">{t("invoices.creditTypeCommercial")}</option>
              <option value="return">{t("invoices.creditTypeReturn")}</option>
              <option value="error">{t("invoices.creditTypeError")}</option>
              <option value="irrecoverable">{t("invoices.creditTypeIrrecoverable")}</option>
            </select>
          </label>
          <label className="text-sm text-neutral-300">
            {t("invoices.tvaLabel")}
            <input
              value={tvaRate}
              onChange={(e) => {
                const v = e.target.value;
                setTvaRate(v);
                const r = parseDecimalInput(v);
                const f = 1 + (Number.isFinite(r) ? r : 0) / 100;
                if (fonte === "ttc") {
                  const c = parseDecimalInput(ttcIn);
                  setHt(ttcIn.trim() && Number.isFinite(c) ? fmt2(c / f) : "");
                } else {
                  const h = parseDecimalInput(ht);
                  setTtcIn(ht.trim() && Number.isFinite(h) ? fmt2(h * f) : "");
                }
              }}
              inputMode="decimal"
              className={inputCls}
            />
          </label>
          {restante != null && (
            <div className="grid grid-cols-2 gap-2" data-testid="avoir-mode">
              <button
                type="button"
                onClick={() => {
                  setModo("complet");
                  mudarHt(restante.toFixed(2).replace(".", ","));
                }}
                className={`rounded-md border px-3 py-2 text-sm ${
                  modo === "complet"
                    ? "border-lime bg-lime/10 text-lime"
                    : "border-neutral-700 text-neutral-200 hover:border-lime"
                }`}
              >
                {t("invoices.avoirFullBtn")}
                <span className="block font-mono text-xs text-neutral-400">
                  {formatMoney(restante, { decimals: 2 })} HT
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setModo("partiel");
                  setHt("");
                  setTtcIn("");
                  setFonte("ht");
                }}
                className={`rounded-md border px-3 py-2 text-sm ${
                  modo === "partiel"
                    ? "border-lime bg-lime/10 text-lime"
                    : "border-neutral-700 text-neutral-200 hover:border-lime"
                }`}
              >
                {t("invoices.avoirPartialBtn")}
              </button>
            </div>
          )}
          <label className="text-sm text-neutral-300">
            {t("invoices.amountHtLabel")}
            <input
              value={ht}
              onChange={(e) => {
                mudarHt(e.target.value);
                setModo("partiel");
              }}
              inputMode="decimal"
              placeholder="0,00"
              className={inputCls + " font-mono"}
              data-testid="avoir-ht"
            />
          </label>
          <label className="text-sm text-neutral-300">
            {t("invoices.ttcInputLabel")}
            <input
              value={ttcIn}
              onChange={(e) => {
                mudarTtc(e.target.value);
                setModo("partiel");
              }}
              inputMode="decimal"
              placeholder="0,00"
              className={inputCls + " font-mono"}
              data-testid="avoir-ttc"
            />
          </label>
        </div>
        <label className="mt-3 block text-sm text-neutral-300">
          {t("invoices.reasonLabel")}
          {reasonRequired && <span className="text-amber-300"> *</span>}
          <input
            value={reason}
            required={reasonRequired}
            data-testid="avoir-reason"
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("invoices.reasonPlaceholder")}
            className={inputCls}
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800"
          >
            {t("invoices.close")}
          </button>
          <button
            onClick={() =>
              createMut.mutate({
                companyId,
                invoiceId: invoice.id,
                creditNoteNumber: num.trim(),
                creditType,
                ...(fonte === "ttc" ? { totalTtc: ttcNum } : { totalHt: htNum }),
                tvaRate: rateNum,
                reason: reason.trim() || undefined,
                issuedAt,
              })
            }
            disabled={!valid || createMut.isPending}
            data-testid="avoir-submit"
            className="rounded-md bg-amber-400 px-4 py-1.5 text-sm font-medium text-neutral-900 hover:opacity-90 disabled:opacity-40"
          >
            {createMut.isPending ? t("invoices.creating") : t("invoices.createAvoirBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
